import { APP_POOL_CONFIG } from '@synergy/config';
import { Divider, FormControlLabel, FormHelperText, Grid, Switch, Theme } from '@mui/material';
import { Field, FieldMetaProps, FieldProps, useFormikContext } from 'formik';
import { FormOfAddress } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PoolBanner } from '@synergy/components-next';
import { PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { ReactNode, useEffect, useState } from 'react';
import { fields } from '@synergy/utils';
import { makeStyles } from '@mui/styles';

import { ContactsLoginsFormikValues } from './models/contacts-logins-formik-values';
import { PoolPositionAutocompleteList } from '../../common/components/autocomplete-list/position-autocomplete-list/pool-position-autocomplete-list';
import { localizedPositionsSelector } from '../../common/selectors/positions-selectors';
import { useSelector } from '../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(5),
    },
    formControlLabel: {
        '& .MuiTypography-root': {
            borderBottom: 'none',
            paddingBottom: 0,
            // FIXME: should be done in the theme or as a variant of the component
            fontSize: 18,
            fontWeight: 600,
            lineHeight: '130%',
        },
    },
    highlight: {
        color: '#2196F3',
    },
}));

export const ContactsLoginsDetailForm = () => {
    const classes = useStyles();
    const formikContext = useFormikContext<ContactsLoginsFormikValues>();
    const orderCeilingsValues = useSelector(state => state.contactsLogins.orderCeilings);
    const isExistingEmail = useSelector(state => state.contactsLogins.isExistingEmail);
    const positions = useSelector(localizedPositionsSelector);
    const fieldNames = fields<ContactsLoginsFormikValues>();
    const [showPositionHint, setShowPositionHint] = useState(formikContext.values.positionId === null);
    const currency = useSelector(state => state.currency);

    // A limit per order at or above the month is cut to it; said under the
    // field for as long as it holds (O-13, R-173).
    const cappedHint = (perOrder?: number | string | null, monthly?: number | string | null): ReactNode => {
        // The select hands its value over as text; -1 stands for "unlimited".
        const amountOf = (value?: number | string | null): number | null =>
            null === value || undefined === value || '' === value || Number(value) < 0 ? null : Number(value);
        const limit = amountOf(perOrder);
        const month = amountOf(monthly);

        return null !== limit && null !== month && limit >= month ? (
            <FormattedMessage
                id="app.contacts.limits.cappedToMonthly"
                values={{ amount: <FormattedNumber value={month} style="currency" currency={currency} /> }}
            />
        ) : null;
    };

    useEffect(() => {
        if (formikContext.values.positionId !== null) {
            setShowPositionHint(false);
        }
    }, [formikContext.values.positionId]);

    const formOfAddressSelectOptions: SelectOption[] = [
        { value: FormOfAddress.Mr, displayValue: <FormattedMessage id="app.common.mr" /> },
        { value: FormOfAddress.Mrs, displayValue: <FormattedMessage id="app.common.mrs" /> },
        {
            value: FormOfAddress.Neutral,
            displayValue: <FormattedMessage id="app.common.neutralName" />,
        },
    ];

    const orderCeilingsSelectOptions = [
        {
            value: -1,
            displayValue: <FormattedMessage id="app.contacts.logins.unlimited" />,
        },
        ...orderCeilingsValues.map<SelectOption>(item => ({
            value: item.toString(),
            displayValue: <FormattedNumber value={item} />,
        })),
    ];

    const getEmailHelperText = (meta: FieldMetaProps<string>) => {
        if (meta.touched && meta.error) {
            return <FormattedMessage id={meta.error} />;
        }
        if (isExistingEmail) {
            return (
                <FormattedMessage
                    id="app.error.existingEmail"
                    values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                />
            );
        }
        return null;
    };

    return (
        <>
            <Grid container={true} spacing={2} className={classes.container}>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.formOfAddress}>
                        {({ field, meta, form }: FieldProps<FormOfAddress>) => {
                            return (
                                <>
                                    <PoolSelect
                                        {...field}
                                        fullWidth={true}
                                        options={formOfAddressSelectOptions}
                                        value={field.value}
                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                        label={<FormattedMessage id="app.common.formOfAddress" />}
                                        required={true}
                                        noSorting={true}
                                        disallowDefaultValue={true}
                                        error={meta.touched && !!meta.error}
                                    />
                                    {meta.error && meta.touched && (
                                        <FormHelperText error={true}>
                                            {<FormattedMessage id={meta.error} />}
                                        </FormHelperText>
                                    )}
                                </>
                            );
                        }}
                    </Field>
                </Grid>
                <Grid item={true} xs={8}>
                    <Field name={fieldNames.positionId}>
                        {({ field, form, meta }: FieldProps<number>) => (
                            <>
                                <PoolPositionAutocompleteList
                                    name={field.name}
                                    label={<FormattedMessage id="app.common.position" />}
                                    required={true}
                                    value={positions.find(position => position.id === field.value)}
                                    onSelected={value => form.setFieldValue(field.name, value?.id ?? null)}
                                    error={meta.touched && !!meta.error}
                                />
                                {meta.error && meta.touched && (
                                    <FormHelperText error={true}>{<FormattedMessage id={meta.error} />}</FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                {showPositionHint && (
                    <Grid item={true} xs={12}>
                        <PoolBanner onClose={() => setShowPositionHint(false)}>
                            <FormattedMessage
                                id="app.employee.position.hint"
                                values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                            />
                        </PoolBanner>
                    </Grid>
                )}
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.lastName}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                required={true}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.lastName" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.firstName}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                required={true}
                                label={<FormattedMessage id="app.common.firstName" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.phone}>
                        {({ field }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.phoneNumber" />}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.email}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.email" />}
                                required={true}
                                error={(meta.touched && !!meta.error) || isExistingEmail}
                                helperText={getEmailHelperText(meta)}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>

            <Grid container={true} style={{ marginBottom: '0.5rem' }}>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.isUser}>
                        {({ field, form }: FieldProps<boolean>) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="switch"
                                        checked={field.value}
                                        onChange={() => form.setFieldValue(field.name, !field.value, false)}
                                        color="primary"
                                        disabled={form.values.isUser}
                                    />
                                }
                                label={<FormattedMessage id="app.common.login" />}
                                className={classes.formControlLabel}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>

            <Divider sx={{ backgroundColor: theme => theme.palette.text.primary, mb: 2 }} />

            <Grid container={true} spacing={2}>
                <Grid item={true} xs={12}>
                    <PoolTypography typography="subtitle1" id="app.contacts.logins.orderCeiling" />
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.monthlyOrderCeiling}>
                        {({ field, meta, form }: FieldProps<number>) => (
                            <>
                                <PoolSelect
                                    {...field}
                                    fullWidth={true}
                                    options={orderCeilingsSelectOptions}
                                    value={field.value?.toString() ?? -1}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    label={<FormattedMessage id="app.common.monthlyOrderCeiling" />}
                                    required={true}
                                    noSorting={true}
                                    disabled={!form.values.isUser}
                                    disallowDefaultValue={true}
                                    error={meta.touched && !!meta.error}
                                />
                                {meta.error && meta.touched && (
                                    <FormHelperText error={true}>{<FormattedMessage id={meta.error} />}</FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.perOrderOrderCeiling}>
                        {({ field, meta, form }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.common.perOrderOrderCeiling" />}
                                error={meta.touched && !!meta.error}
                                readOnly={!form.values.isUser}
                                helperText={
                                    meta.touched && meta.error ? (
                                        <FormattedMessage id="app.error.number" />
                                    ) : (
                                        cappedHint(field.value, form.values.monthlyOrderCeiling)
                                    )
                                }
                                isNumeric={true}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>
        </>
    );
};
